import { useEffect, useState, useCallback } from "react";
import { api } from "../utils/api";
import { renderNewsHtml, isRichTextEmpty } from "../utils/sanitize";

/**
 * Übergangs-Bereich zwischen Hero und Kontakt-Bar.
 * Zeigt IMMER den blauen Verlauf (Hero-Ende).
 * Wenn aktive News-Items vorhanden sind, erscheint für jeden ein goldener Rahmen.
 *
 * Refetcht automatisch bei:
 *   - Component-Mount (initial)
 *   - Tab wird wieder sichtbar (visibilitychange)
 *   - Fenster erhält Fokus zurück (focus)
 *   - Regelmäßig alle 5 Minuten (Fallback für Long-Living-Tabs)
 * → News, die im Backend live geschaltet werden, erscheinen ohne Reload.
 */
export default function NewsBanner() {
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      // Cache-Busting: verhindert dass Browser eine veraltete Response ausliefert,
      // wenn der Bereich schnell abgelaufen/verlängert wird.
      const { data } = await api.getPublicNews({ bust: Date.now() });
      if (data && data.ok) setItems(Array.isArray(data.items) ? data.items : []);
    } catch { /* ignorieren */ }
  }, []);

  useEffect(() => {
    load();
    const onVisibility = () => { if (document.visibilityState === "visible") load(); };
    const onFocus = () => load();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    const interval = setInterval(load, 5 * 60 * 1000); // 5 Min
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      clearInterval(interval);
    };
  }, [load]);

  const visible = items.filter((it) => {
    const subj = (it.subject || "").trim();
    return subj !== "" || !isRichTextEmpty(it.body);
  });
  const hasNews = visible.length > 0;

  return (
    <div
      className="news-banner"
      data-testid="news-banner"
      role={hasNews ? "region" : undefined}
      aria-label={hasNews ? "Aktuelle Praxis-Info" : undefined}
    >
      {visible.map((it) => {
        const align = ["left", "center", "right"].includes(it.subject_align)
          ? it.subject_align : "left";
        const subject = (it.subject || "").trim();
        const bodyEmpty = isRichTextEmpty(it.body);
        return (
          <div key={it.id} className="news-banner-inner" data-testid={`news-banner-item-${it.id}`}>
            {subject && (
              <h2
                className="news-banner-subject"
                data-testid="news-banner-subject"
                style={{ textAlign: align }}
              >
                {subject}
              </h2>
            )}
            {!bodyEmpty && (
              <div
                className="news-banner-body"
                data-testid="news-banner-body"
                dangerouslySetInnerHTML={{ __html: renderNewsHtml(it.body) }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
